import React from 'react';

import {
  ChipField,
  Datagrid,
  DateField,
  FunctionField,
  Loading,
  RecordContextProvider,
  ReferenceField,
  SingleFieldList,
  useGetOne,
  usePermissions,
  useRecordContext,
} from 'react-admin';

import { Grid } from '@mui/material';

import NoResults from '../layout/NoResults';
import TextDiffField from '../utils/auditTracks/TextDiffField';
import DateDiffField from '../utils/auditTracks/DateDiffField';
import StatusDiffField from '../utils/auditTracks/StatusDiffField';
import ReferenceDiffField from '../utils/auditTracks/ReferenceDiffField';
import auditTrackDiffGenerator, { AuditTrack } from '../audit/auditDiffGenerator/auditTrackDiffGenerator';
import AuditExtraInfo from '../audit/AuditExtraInfo';
import ArrayDiffField from '../utils/auditTracks/ArrayDiffField';
import ReferenceArrayDiffField from '../utils/auditTracks/ReferenceArrayDiffField';
import ReferenceEntityField from '../../customFields/ReferenceEntityField';
import EntityDisplayNameField from '../../customFields/EntityDisplayNameField';
import RuleReferenceName from '../rules/components/RuleReferenceName';
import { Ping } from '../../utilities/schemas/ping';
import resourceNames from '../../constants/resourceNames';
import hasAccess from '../../utilities/hasAccess';
import PermissionMissing from '../utils/PermissionMissing';
import { boldDataGridStyle } from '../../constants/style/datagridStyles';
import { CommentMessageField } from '../comment/CommentList';
import actions, { Actions } from '../../constants/actions';

const PingAuditDetails = () => {
  const record = useRecordContext<AuditTrack<Ping>>();
  const { data, isLoading } = useGetOne<AuditTrack<Ping>>(`audit-tracks/${record?.id}/previous`, { id: '' });
  const { permissions } = usePermissions<Actions[]>();

  if (!record) return <NoResults />;

  if (!data) {
    if (isLoading) return <Loading />;
    if (record.extraInfo) return <AuditExtraInfo />;
    return <NoResults />;
  }

  const isElementCreated = data.id === record.id;

  if (!record.documentAfter || (!isElementCreated && !data.documentAfter)) {
    if (record.extraInfo) return <AuditExtraInfo />;
    return <NoResults />;
  }

  const auditTracksDiff = auditTrackDiffGenerator<Ping>({
    current: record,
    previous: !isElementCreated ? data : undefined,
  });

  const { resourceData } = auditTracksDiff;

  const hasFieldChanges = {
    createdAt: resourceData.createdAt?.hasChanged,
    description: resourceData.description?.hasChanged,
    status: resourceData.status?.hasChanged,
    isBinaryRule: resourceData.isBinaryRule?.hasChanged,
    rule: resourceData.rule?.hasChanged,
    riskExpiryDate: resourceData.riskExpiryDate?.hasChanged,
    riskStartDate: resourceData.riskStartDate?.hasChanged,
    score: resourceData.score?.hasChanged,
    overrideRecommendation: resourceData.overrideRecommendation?.hasChanged,
    isTestPing: resourceData.isTestPing?.hasChanged,
    request: resourceData.request?.hasChanged,
    entities: resourceData.entities?.hasChanged,
    tags: resourceData.tags?.hasChanged,
    comments: resourceData.comments?.hasChanged,
  };

  const hasAnyChanges = Object.values(hasFieldChanges).some((field) => field);

  return (
    <Grid container spacing={6}>
      {!hasAnyChanges && <NoResults text="Only system changes detected" />}
      {hasAnyChanges && (
        <RecordContextProvider value={resourceData}>
          {resourceData.createdAt?.hasChanged && (
            <DateDiffField source="createdAt" label="Created At" showTime />
          )}
          {resourceData.description?.hasChanged && (
            <TextDiffField source="description" label="Description" />
          )}
          {resourceData.status?.hasChanged && (
            <StatusDiffField source="status" label="Status" />
          )}
          {resourceData.isBinaryRule?.hasChanged && (
            <TextDiffField source="isBinaryRule" label="Is Binary Rule" />
          )}
          {resourceData.rule?.hasChanged && (
            <ReferenceDiffField
              source="rule"
              label="Rule"
              reference={resourceNames.RULES}
            >
              <RuleReferenceName />
            </ReferenceDiffField>
          )}
          {resourceData.riskExpiryDate?.hasChanged && (
            <DateDiffField source="riskExpiryDate" label="Risk Expiry Date" showTime />
          )}
          {resourceData.riskStartDate?.hasChanged && (
            <DateDiffField source="riskStartDate" label="Risk Start Date" showTime />
          )}
          {resourceData.score?.hasChanged && (
            <TextDiffField source="score" label="Score" />
          )}
          {resourceData.overrideRecommendation?.hasChanged && (
            <TextDiffField source="overrideRecommendation" label="Override Recommendation" />
          )}
          {resourceData.request?.hasChanged && (
            <ReferenceDiffField
              source="request"
              label="Request"
              reference={resourceNames.REQUESTS}
            >
              <FunctionField
                render={(request?: { customId?: string; id?: string }) => `${request?.customId ?? request?.id ?? '-'}`}
              />
            </ReferenceDiffField>
          )}
          {resourceData.isTestPing?.hasChanged && (
            <TextDiffField source="isTestPing" label="Is Test Ping" />
          )}
          {resourceData.entities?.hasChanged && (
            <ArrayDiffField source="entities" label="Entities">
              <SingleFieldList linkType={false}>
                <ReferenceEntityField source="">
                  <EntityDisplayNameField chip />
                </ReferenceEntityField>
              </SingleFieldList>
            </ArrayDiffField>
          )}
          {resourceData.tags?.hasChanged && (
            <ReferenceArrayDiffField
              source="tags"
              label="Tags"
              reference={resourceNames.TAGS}
            >
              <SingleFieldList linkType={false}>
                <ChipField source="name" size="small" />
              </SingleFieldList>
            </ReferenceArrayDiffField>
          )}
          {resourceData.comments?.hasChanged && (
            <ArrayDiffField source="comments" label="Comments" width="100%" size={{ xs: 12 }}>
              <Datagrid sx={boldDataGridStyle} bulkActionButtons={false}>
                <CommentMessageField emptyText="-" label="Comment" />
                <DateField showTime source="date" />
                {hasAccess(permissions, actions.MEMBER_GET) ? (
                  <ReferenceField label="Author" source="member" reference={resourceNames.MEMBERS} link="edit">
                    <FunctionField render={(member: { firstName: string; lastName: string }) => `${member.firstName} ${member.lastName}`} />
                  </ReferenceField>
                ) : (<PermissionMissing />)}
              </Datagrid>
            </ArrayDiffField>
          )}
        </RecordContextProvider>
      )}
      {record.extraInfo && <AuditExtraInfo />}
    </Grid>
  );
};

export default PingAuditDetails;
